import { vendorStore } from "@/store/vendor.js"

const WEEK_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
const DAY_IN_MS = 24 * 60 * 60 * 1000
const MINUTE_IN_MS = 60 * 1000
class VendorHelper {
  getStore() {
    return vendorStore();  // Lazily access the store when needed
  }
  getVendorLocalMoment(timezone = "UTC") {
    try {
      // Get the actual current time as Date
      const now = new Date()

      // Format current time in vendor's timezone for local components
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        year: "numeric", month: "numeric", day: "numeric",
        hour: "numeric", minute: "numeric", second: "numeric",
        hour12: false,
        weekday: "long"
      })

      const parts = formatter.formatToParts(now)
      const bag = {}
      parts.forEach(({ type, value }) => {
        bag[type] = value
      })

      const year = parseInt(bag.year, 10)
      if (Number.isNaN(year)) {
        throw new Error("Invalid timezone data")
      }
      const month = parseInt(bag.month, 10)
      const day = parseInt(bag.day, 10)
      const hour = parseInt(bag.hour, 10)
      const minute = parseInt(bag.minute, 10)
      const second = parseInt(bag.second, 10)

      // Calculate offset mathematically instead of parsing string
      // 1. Create a UTC date representing the 'Face Value' of the local time
      const localTimeOnUtcScale = Date.UTC(year, month - 1, day, hour, minute, second)
      // 2. Get the actual UTC timestamp (floored to seconds to match precision)
      const actualUtcTimestamp = Math.floor(now.getTime() / 1000) * 1000

      // 3. Difference is the offset in milliseconds
      const offsetMs = localTimeOnUtcScale - actualUtcTimestamp

      // 4. Convert to minutes (round to nearest to handle any sub-second drift)
      const offsetMinutes = Math.round(offsetMs / 60000)

      return {
        date: now,
        timezone,
        offsetMinutes,
        dayName: bag.weekday,
        localComponents: { year, month, day, hour, minute, second }
      }
    } catch (error) {
      console.error("Failed to resolve vendor timezone. Falling back to UTC.", error)
      const date = new Date()
      return {
        date,
        timezone: "UTC",
        offsetMinutes: 0,
        dayName: WEEK_DAYS[date.getUTCDay()],
        localComponents: {
          year: date.getUTCFullYear(),
          month: date.getUTCMonth() + 1,
          day: date.getUTCDate(),
          hour: date.getUTCHours(),
          minute: date.getUTCMinutes(),
          second: date.getUTCSeconds()
        }
      }
    }
  }
  parseTimezoneOffset(label = "GMT+00:00") {
    if (!label || label === "UTC") {
      return 0
    }
    const match = label.match(/GMT([+-])(\d{2})(?::?(\d{2}))?/)
    if (!match) {
      return 0
    }
    const sign = match[1] === "-" ? -1 : 1
    const hours = parseInt(match[2], 10) || 0
    const minutes = parseInt(match[3] || "0", 10)
    return sign * (hours * 60 + minutes)
  }
  getDayIndex(dayName) {
    const idx = WEEK_DAYS.indexOf(dayName)
    return idx === -1 ? 0 : idx
  }
  normalizeDayIndex(index) {
    const total = WEEK_DAYS.length
    return ((index % total) + total) % total
  }
  getDayNameByIndex(index) {
    return WEEK_DAYS[this.normalizeDayIndex(index)]
  }
  getAdjacentDayName(dayName, offset = -1) {
    const idx = this.getDayIndex(dayName)
    return this.getDayNameByIndex(idx + offset)
  }
  getVendorDayStart(moment, offsetDays = 0) {
    const { localComponents, offsetMinutes } = moment
    // Calculate midnight in vendor's LOCAL timezone, then convert to UTC
    // If vendor is UTC+11, their midnight is 11 hours BEFORE UTC midnight
    // So: local midnight = UTC midnight - offset (when offset is positive)
    // Example: Midnight Sydney (UTC+11) = 13:00 previous day UTC
    const localMidnightAsUtc = Date.UTC(localComponents.year, localComponents.month - 1, localComponents.day, 0, 0, 0)
    const utcMidnight = localMidnightAsUtc - (offsetMinutes * MINUTE_IN_MS)
    return new Date(utcMidnight + offsetDays * DAY_IN_MS)
  }
  convertDisplayTimeToMinutes(timeString = "") {
    if (!timeString) {
      return 0
    }
    const [time, period] = timeString.trim().split(" ")
    const [hourPart, minutePart = "0"] = time.split(":")
    let hours = parseInt(hourPart, 10)
    const minutes = parseInt(minutePart, 10)
    const suffix = (period || "").toLowerCase()
    if (suffix === "pm" && hours !== 12) {
      hours += 12
    }
    if (suffix === "am" && hours === 12) {
      hours = 0
    }
    return hours * 60 + minutes
  }
  buildPeriodsForDay(workDay, baseUtc) {
    if (!workDay || workDay.status !== 1 || !Array.isArray(workDay.period_list)) {
      return []
    }
    const interval = parseInt(workDay.time_interval, 10) || 15
    return workDay.period_list
      .filter(period => period.start_time && period.end_time)
      .map(period => {
        const startMinutes = this.convertDisplayTimeToMinutes(period.start_time)
        let endMinutes = this.convertDisplayTimeToMinutes(period.end_time)
        let crossesMidnight = false
        if (endMinutes <= startMinutes) {
          endMinutes += 24 * 60
          crossesMidnight = true
        }
        const start = new Date(baseUtc.getTime() + startMinutes * MINUTE_IN_MS)
        const end = new Date(baseUtc.getTime() + endMinutes * MINUTE_IN_MS)
        return { start, end, crossesMidnight, interval, raw: period, sourceDay: workDay.day_index }
      })
      .filter(period => period.start < period.end)
  }
  isDateWithinPeriods(date, periods = []) {
    if (!periods.length) {
      return false
    }
    return periods.some(period => date >= period.start && date < period.end)
  }
  alignDateToInterval(referenceMs, intervalMinutes, dayStartMs) {
    const elapsedMinutes = Math.max(0, Math.floor((referenceMs - dayStartMs) / MINUTE_IN_MS))
    const rounded = Math.ceil(elapsedMinutes / intervalMinutes) * intervalMinutes
    return new Date(dayStartMs + rounded * MINUTE_IN_MS)
  }
  formatSlotDisplay(date, timezone) {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    }).format(date)
  }
  formatDateDisplay(date, timezone) {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      weekday: "short",
      month: "short",
      day: "numeric"
    }).format(date)
  }
  generateUpcomingSlots(workingDays = [], timezone = "UTC", options = {}) {
    if (!Array.isArray(workingDays) || workingDays.length === 0) {
      return []
    }
    const daysToGenerate = options.days || 7
    const previewLimit = options.previewLimit ?? 6
    const referenceMoment = options.referenceMoment || this.getVendorLocalMoment(timezone)
    const startDayIndex = this.getDayIndex(referenceMoment.dayName)
    const dayStartUtc = this.getVendorDayStart(referenceMoment, 0)
    const slotsByDay = []
    const aggregatedPeriods = []
    for (let offset = -1; offset <= daysToGenerate; offset++) {
      const dayName = this.getDayNameByIndex(startDayIndex + offset)
      const workDay = workingDays.find(day => day.day_index === dayName)
      if (!workDay) {
        continue
      }
      const baseUtc = new Date(dayStartUtc.getTime() + offset * DAY_IN_MS)
      aggregatedPeriods.push(...this.buildPeriodsForDay(workDay, baseUtc))
    }
    for (let offset = 0; offset < daysToGenerate; offset++) {
      const dayStart = new Date(dayStartUtc.getTime() + offset * DAY_IN_MS)
      const dayEnd = new Date(dayStart.getTime() + DAY_IN_MS)
      const dayName = this.getDayNameByIndex(startDayIndex + offset)
      const relevant = aggregatedPeriods.filter(period => period.end > dayStart && period.start < dayEnd)
      if (!relevant.length) {
        continue
      }
      let totalSlots = 0
      const previewSlots = []
      relevant.forEach(period => {
        const interval = period.interval || 15
        let slotStart = new Date(Math.max(period.start.getTime(), dayStart.getTime()))
        const slotEnd = new Date(Math.min(period.end.getTime(), dayEnd.getTime()))
        if (offset === 0) {
          const nowMs = referenceMoment.date.getTime()
          if (nowMs >= slotEnd.getTime()) {
            return
          }
          if (nowMs > slotStart.getTime()) {
            slotStart = this.alignDateToInterval(nowMs, interval, dayStart.getTime())
            if (slotStart < period.start) {
              slotStart = new Date(Math.max(period.start.getTime(), dayStart.getTime()))
            }
            if (slotStart >= slotEnd) {
              return
            }
          }
        }
        const availableMinutes = Math.max(0, slotEnd.getTime() - slotStart.getTime())
        const slotsInPeriod = Math.max(0, Math.ceil(availableMinutes / (interval * MINUTE_IN_MS)))
        totalSlots += slotsInPeriod
        let previewCursor = new Date(slotStart)
        for (let i = 0; i < slotsInPeriod && previewSlots.length < previewLimit; i += 1) {
          previewSlots.push({
            label: this.formatSlotDisplay(previewCursor, timezone),
            iso: previewCursor.toISOString(),
            timestamp: previewCursor.getTime()
          })
          previewCursor = new Date(previewCursor.getTime() + interval * MINUTE_IN_MS)
        }
      })
      if (totalSlots > 0) {
        slotsByDay.push({
          dayName,
          dateKey: dayStart.toISOString().slice(0, 10),
          displayDate: this.formatDateDisplay(dayStart, timezone),
          slots: previewSlots,
          totalSlots,
          remainingCount: Math.max(totalSlots - previewSlots.length, 0)
        })
      }
    }
    return slotsByDay
  }

  /**
   * Get availability snapshot for vendor.
   * @param {Object} vendorData - Vendor data object
   * @param {Object|Array} workingDaysData - New format: { selling_timeslot, delivery_timeslot, pickup_timeslot } or legacy array
   * @param {Object} options - Additional options including 'mode' ('selling', 'delivery', 'pickup')
   */
  getAvailabilitySnapshot(vendorData = {}, workingDaysData = {}, options = {}) {
    const mode = options.mode || 'selling'

    // Handle both new object format and legacy array format
    let sellingSlots = []
    let deliverySlots = []
    let pickupSlots = []

    if (Array.isArray(workingDaysData)) {
      // Legacy format
      sellingSlots = workingDaysData
      deliverySlots = workingDaysData
      pickupSlots = workingDaysData
    } else if (workingDaysData && typeof workingDaysData === 'object') {
      // New categorized format
      sellingSlots = workingDaysData.selling_timeslot || []
      deliverySlots = workingDaysData.delivery_timeslot || []
      pickupSlots = workingDaysData.pickup_timeslot || []
    }

    // Select which timeslots to use based on mode
    const workingDays = mode === 'delivery' ? deliverySlots : mode === 'pickup' ? pickupSlots : sellingSlots

    if (!vendorData || !Array.isArray(workingDays) || workingDays.length === 0) {
      return {
        timezone: vendorData?.timezone || "UTC",
        now: null,
        dayName: null,
        isOpenNow: false,
        scheduleOpen: false,
        vendorOnline: vendorData?.vendor_status === 1,
        pickupNowAvailable: false,
        deliveryNowAvailable: false,
        slotsByDay: [],
        nextSlot: null
      }
    }
    const timezone = vendorData.timezone || "UTC"
    const referenceMoment = options.referenceMoment || this.getVendorLocalMoment(timezone)
    const startDayIndex = this.getDayIndex(referenceMoment.dayName)
    const dayStartUtc = this.getVendorDayStart(referenceMoment, 0)

    // Check if vendor is currently open based on SELLING timeslot
    const sellingTodayPeriods = []
    for (let offset = -1; offset <= 0; offset++) {
      const dayName = this.getDayNameByIndex(startDayIndex + offset)
      const workDay = sellingSlots.find(day => day.day_index === dayName)
      if (!workDay) {
        continue
      }
      const baseUtc = new Date(dayStartUtc.getTime() + offset * DAY_IN_MS)
      sellingTodayPeriods.push(...this.buildPeriodsForDay(workDay, baseUtc))
    }
    const sellingScheduleOpen = this.isDateWithinPeriods(referenceMoment.date, sellingTodayPeriods)

    // Check delivery availability based on DELIVERY timeslot
    const deliveryTodayPeriods = []
    for (let offset = -1; offset <= 0; offset++) {
      const dayName = this.getDayNameByIndex(startDayIndex + offset)
      const workDay = deliverySlots.find(day => day.day_index === dayName)
      if (!workDay) {
        continue
      }
      const baseUtc = new Date(dayStartUtc.getTime() + offset * DAY_IN_MS)
      deliveryTodayPeriods.push(...this.buildPeriodsForDay(workDay, baseUtc))
    }
    const deliveryScheduleOpen = this.isDateWithinPeriods(referenceMoment.date, deliveryTodayPeriods)

    // Check pickup availability based on PICKUP timeslot
    const pickupTodayPeriods = []
    for (let offset = -1; offset <= 0; offset++) {
      const dayName = this.getDayNameByIndex(startDayIndex + offset)
      const workDay = pickupSlots.find(day => day.day_index === dayName)
      if (!workDay) {
        continue
      }
      const baseUtc = new Date(dayStartUtc.getTime() + offset * DAY_IN_MS)
      pickupTodayPeriods.push(...this.buildPeriodsForDay(workDay, baseUtc))
    }
    const pickupScheduleOpen = this.isDateWithinPeriods(referenceMoment.date, pickupTodayPeriods)

    const vendorOnline = vendorData.vendor_status === 1
    const isOpenNow = vendorOnline && sellingScheduleOpen

    // Generate slots based on the selected mode's timeslots
    const slotsByDay = vendorOnline ? this.generateUpcomingSlots(workingDays, timezone, { ...options, referenceMoment }) : []
    const firstDay = slotsByDay[0]
    const nextSlot = firstDay && firstDay.slots.length
      ? { ...firstDay.slots[0], dayName: firstDay.dayName, displayDate: firstDay.displayDate }
      : null
    return {
      timezone,
      now: referenceMoment,
      dayName: referenceMoment.dayName,
      isOpenNow,
      scheduleOpen: sellingScheduleOpen,
      vendorOnline,
      pickupNowAvailable: vendorOnline && pickupScheduleOpen && vendorData.pickup_status === 1,
      deliveryNowAvailable: vendorOnline && deliveryScheduleOpen && vendorData.delivery_status === 1,
      slotsByDay,
      nextSlot
    }
  }

  /**
   * Get next opening or closing time for a given timeslot array
   * @param {Array} timeslots - Array of day timeslots (e.g., selling_timeslot, delivery_timeslot, pickup_timeslot)
   * @param {string} timezone - Vendor timezone
   * @returns {Object} { isOpen: boolean, nextTime: string, nextAction: 'opens'|'closes', nextDate: Date|null }
   */
  getNextOpenClose(timeslots, timezone = "UTC") {
    if (!Array.isArray(timeslots) || timeslots.length === 0) {
      return { isOpen: false, nextTime: null, nextAction: null, nextDate: null }
    }

    const referenceMoment = this.getVendorLocalMoment(timezone)
    const startDayIndex = this.getDayIndex(referenceMoment.dayName)
    const dayStartUtc = this.getVendorDayStart(referenceMoment, 0)
    const now = referenceMoment.date

    // Build periods for today and yesterday (for overnight periods)
    const allPeriods = []
    for (let offset = -1; offset <= 7; offset++) {
      const dayName = this.getDayNameByIndex(startDayIndex + offset)
      const workDay = timeslots.find(day => day.day_index === dayName)
      if (!workDay) continue
      const baseUtc = new Date(dayStartUtc.getTime() + offset * DAY_IN_MS)
      allPeriods.push(...this.buildPeriodsForDay(workDay, baseUtc))
    }

    // Sort periods by start time
    allPeriods.sort((a, b) => a.start.getTime() - b.start.getTime())

    // Check if currently within any period
    const currentPeriod = allPeriods.find(period => now >= period.start && now < period.end)

    if (currentPeriod) {
      // Currently open - return closing time
      return {
        isOpen: true,
        nextTime: this.formatSlotDisplay(currentPeriod.end, timezone),
        nextAction: 'closes',
        nextDate: currentPeriod.end
      }
    }

    // Not open - find next opening time
    const nextPeriod = allPeriods.find(period => period.start > now)

    if (nextPeriod) {
      // Format with day if not today
      // Format with day always to avoid confusion
      const timeStr = this.formatSlotDisplay(nextPeriod.start, timezone)
      const displayTime = `${nextPeriod.sourceDay} ${timeStr}`

      return {
        isOpen: false,
        nextTime: displayTime,
        nextAction: 'opens',
        nextDate: nextPeriod.start
      }
    }

    // No upcoming periods found
    return { isOpen: false, nextTime: null, nextAction: null, nextDate: null }
  }

  /**
   * Check if two dates are on the same day in a given timezone
   */
  isSameDay(date1, date2, timezone) {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    })
    return formatter.format(date1) === formatter.format(date2)
  }
  isPastSelectedTime(selectedDateTime) {
    const selectedDate = new Date(selectedDateTime);
    const currentDate = new Date();
    if (
      selectedDate.getDate() === currentDate.getDate() &&
      selectedDate.getMonth() === currentDate.getMonth() &&
      selectedDate.getFullYear() === currentDate.getFullYear()
    ) {
      return selectedDate < currentDate;
    }
    return false;
  }


  convertTo24HourFormat(time) {
    const [hours, minutes] = time.split(':');
    let hour = parseInt(hours, 10);
    const isPM = time.toLowerCase().includes('pm');

    if (isPM && hour !== 12) {
      hour += 12;
    } else if (!isPM && hour === 12) {
      hour = 0;
    }

    return new Date().setHours(hour, parseInt(minutes, 10), 0, 0);
  }

  isOpen(currentTime, todayTiming) {
    const store = this.getStore()
    // Handle both new object format and legacy array format
    const wd = store?.workingDays
    const hasWorkingDays = wd && (Array.isArray(wd) ? wd.length > 0 : (wd.selling_timeslot && wd.selling_timeslot.length > 0))
    if (store?.resturant && hasWorkingDays) {
      const snapshot = this.getAvailabilitySnapshot(store.resturant, wd)
      return snapshot.isOpenNow
    }
    if (todayTiming?.status === 1) {
      const now = this.convertTo24HourFormat(currentTime);
      const open = this.convertTo24HourFormat(todayTiming.period_list[0].start_time);
      const close = this.convertTo24HourFormat(todayTiming.period_list[todayTiming.period_list.length - 1].end_time);
      const check = now >= open && now <= close;
      if (check) {
        return this.isCurrentTimeInRange(todayTiming.period_list, currentTime)
      } else {
        return false;
      }
    }
    return false;
  }
  isCurrentTimeInRange(timesArray, currentTimeString) {
    const currentMinutes = this.convertDisplayTimeToMinutes(currentTimeString);
    for (const period of timesArray) {
      const startMinutes = this.convertDisplayTimeToMinutes(period.start_time);
      let endMinutes = this.convertDisplayTimeToMinutes(period.end_time);
      if (endMinutes <= startMinutes) {
        if (currentMinutes >= startMinutes || currentMinutes < (endMinutes % (24 * 60))) {
          return true;
        }
        continue;
      }
      if (currentMinutes >= startMinutes && currentMinutes < endMinutes) {
        return true;
      }
    }
    return false;
  }



  parseTimeString(timeString) {
    const [time, meridiem] = timeString.split(' ');
    const [hours, minutes, seconds] = time.split(/[:]/).map(Number);
    const date = new Date();
    let adjustedHours = (meridiem.toLowerCase() === 'pm' && hours !== 12) ? hours + 12 : hours;
    if (meridiem.toLowerCase() === 'am' && hours === 12) {
      adjustedHours = 0;
    }
    date.setHours(adjustedHours);
    date.setMinutes(minutes || 0);
    date.setSeconds(seconds || 0);

    return date;
  }

  isVenderOnOrOff(todayTiming, isOpen, vendor_status) {
    const store = this.getStore()
    // Handle both new object format and legacy array format
    const wd = store?.workingDays
    const hasWorkingDays = wd && (Array.isArray(wd) ? wd.length > 0 : (wd.selling_timeslot && wd.selling_timeslot.length > 0))
    if (store?.resturant && hasWorkingDays) {
      const snapshot = this.getAvailabilitySnapshot(store.resturant, wd)
      return snapshot.isOpenNow
    }
    if (todayTiming.status === 0 || !isOpen || vendor_status === 0) {
      return false;
    } else {
      return true;
    }
  }

  setTimeZone(timezone) {
    const moment = this.getVendorLocalMoment(timezone)
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: moment.timezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    })
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: moment.timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    return [moment.dayName, timeFormatter.format(moment.date), dateFormatter.format(moment.date)]
  }
  updatePageTitle(title) {
    document.title = title;
  }

  compareDates(providedDateStr) {
    const providedDate = new Date(providedDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (providedDate > today) {
      console.log("Provided date is in the future.");
      return false;
    } else if (providedDate < today) {
      console.log("Provided date is in the past.");
      return false;
    } else {
      console.log("Provided date is today.");
      return true;
    }
  }
  // formatDateTime
  formatDateTime(array) {
    const [dayOfWeek, timeString, dateString] = array;
    const dateTime = new Date(`${dateString} ${timeString}`);
    return dateTime.toString();
  }

  formatCurrency(price) {
    const store = this.getStore();
    const symbol = store.resturant.currency_symbol;
    const position = store.resturant.currency_symbol_position;
    if (symbol === "none") {
      return price;
    }
    switch (position) {
      case "post":
        return `${price}${symbol}`;
      case "pre":
        return `${symbol} ${price}`;
      default:
        return price;
    }
  }
  isCurrentTimeBefore(targetTime) {
    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    const [targetHours, targetMinutes] = targetTime;
    if (currentHours < targetHours) {
      return true;
    } else if (currentHours === targetHours) {
      return currentMinutes < targetMinutes;
    } else {
      return false;
    }
  }
  generateUniqueId() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789abcdefghijklmnopqrstuvwxyz';
    let result = '';
    for (let i = 0; i < 9; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }
  storeAppVersion() {
    // Legacy: removed. New version control is via public/version.json
    localStorage.removeItem('app_version');
  }

  getAppVersionFromStorage() {
    return '4.0.0'; // Return safe default
  }
  removeCatche() {
    localStorage.removeItem('vendor');
    localStorage.removeItem('app_version');
    localStorage.removeItem('posItems');
    localStorage.removeItem('cartData');
    localStorage.removeItem('free_delivery_amount');
    localStorage.removeItem('deliveryChargeData');
    localStorage.removeItem('deliveryCahrge');
    localStorage.removeItem('deliveryType');
    localStorage.removeItem('dateTimeValue');
    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
    localStorage.removeItem('payment_setting');

  }


  convertTo12Hour(time24) {
    let [hours, minutes, seconds] = time24.split(":");
    hours = parseInt(hours);
    const period = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${minutes}:${seconds} ${period}`;
  }

}
export default VendorHelper = new VendorHelper();


